import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { JobPartCategory } from "../../models/Job";
import ContactFromId from "../contacts/ContactFromId";
import formatDateTime from "../../utils/formatDateTime";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import RsCoreImage from "../core/RsCoreImage";
import Modal from "../../layouts/Modal";
import { ArchiveBoxIcon, ClipboardDocumentListIcon, DocumentTextIcon, MapIcon, MapPinIcon, PencilIcon, PencilSquareIcon, QuestionMarkCircleIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import PartConditionPickerButton from "../shared/PartConditionPicker/PartConditionPickerButton";
import useSinglePart from "../../data/useSinglePart";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import SelectInput from "../shared/Inputs/SelectInput";
import { useForm } from "react-hook-form";
import NotesOutput from "../shared/Notes/NotesOutput";
import { ResourceType } from "../../models/Note";
import { Assignee, IPart, PhysicalRouting } from "../../models/Part";
import JobPartAspects from "./JobPartAspects";
import VehicleLocationDropdown from "../vehicles/VehicleLocationDropdown";
import { IVehicle } from "../../models/Vehicle";
import WarehouseLocationDropdown from "../warehouseLocations/WarehouseLocationDropdown";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import useFlowTypeAccounts from "../../data/useFlowTypeAccounts";
import { AccountType } from "../../models/account/IAccount";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import ScanQrCode from "../shared/ScanQrCode";


interface JobPartProps {
  partCategory: JobPartCategory;
  markAsRemoved: (partData: JobPartCategory['partData']) => void;
  setDamaged: (partData: JobPartCategory['partData']) => void;
  setDamagedOpen: (open: boolean) => void;
  setMissing: (partData: JobPartCategory['partData']) => void;
  setMissingOpen: (open: boolean) => void;
  markNotAccessible: (partData: JobPartCategory['partData']) => void;
  handlePrint: (partData: JobPartCategory['partData']) => void;
  selectedPartIds: Set<string>;
  togglePartSelection: (partId: string | null | undefined) => void;
  onDiagramClick?: (partCategory: JobPartCategory) => void;
  /**
   * Station Manager's dense card: title, tag, account (text with a pencil to
   * edit), QR and the action buttons only - no MPN / grade / weight / notes
   * inputs - so four fit across a floor terminal.
   */
  compact?: boolean;
}

export default function JobPart({ partCategory, markAsRemoved, setDamaged, setDamagedOpen, setMissing, setMissingOpen, markNotAccessible, handlePrint, selectedPartIds, togglePartSelection, onDiagramClick, compact = false }: JobPartProps) {
  const { t } = useTranslation("jobs");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Part"));
  const { patchPart } = useSinglePartMutations(partCategory.partDataId ?? "")
  const { flowTypeAccounts } = useFlowTypeAccounts("JobPart");
  const hasTmeFeature = CheckFeatureFlag("TME");
  const { register, watch, setValue: setFormValue } = useForm<Record<string, any>>({
    defaultValues: { grade: "", accountId: "" },
  });

  const [open, setOpen] = useState(false);
  const [isSafetySelected, setIsSafetySelected] = useState(true);
  const [gradeInfoOpen, setGradeInfoOpen] = useState(false);
  const [storageOpen, setStorageOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [conditionOpen, setConditionOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  // Compact card shows the account as text; the pencil swaps the picker in.
  const [accountEditing, setAccountEditing] = useState(false);

  const [partData, setPartData] = useState<IPart | null>(partCategory.partData ?? null);

  useEffect(() => {
    setPartData(partCategory.partData ?? null);
  }, [partCategory.partData]);

  useEffect(() => {
    setFormValue("grade", partData?.grade ?? "");
  }, [partData?.grade, setFormValue]);

  useEffect(() => {
    setFormValue("accountId", partData?.accountId ?? "");
  }, [partData?.accountId, setFormValue]);

  // Flow-type accounts the part can be assigned to. A blank option lets the
  // user clear the assignment.
  const accountOptions = [
    { id: "", name: t("No Account") },
    ...(flowTypeAccounts.data?.data ?? []).map((account) => ({
      id: account.id,
      name: account.name,
    })),
  ];

  const partCategoryDefault = partData?.category?.partCategoryDefaults?.[0];
  const ebayCategoryId = partCategoryDefault?.ebayCategoryId ?? "";
  const dismantlerAspectNames = (partCategoryDefault?.aspectAssignees ?? [])
    .filter((aspectAssignee) => aspectAssignee.assignee === Assignee.Dismantler && aspectAssignee.aspectName)
    .map((aspectAssignee) => aspectAssignee.aspectName as string);

  const isSelected = partData?.id ? selectedPartIds.has(partData.id) : false;
  const isDeleted = !!(partData as any)?.deleted;

  // Card background reflects the part's state: red for damaged/missing (or a
  // deleted part), green once removed, and amber while it's assigned to a
  // non-recycling account and still needs attention.
  const assignedAccount = partData?.accountId
    ? flowTypeAccounts.data?.data?.find((account) => account.id === partData.accountId)
    : undefined;
  const isNonRecycleAccount =
    !!partData?.accountId &&
    !!flowTypeAccounts.data?.data &&
    assignedAccount?.accountType !== AccountType.Recycling;
  const cardBackground = isDeleted || partData?.isDamaged || partData?.isMissing || partData?.isNotAccessible
    ? "bg-red-100"
    : partData?.removedOn
      ? "bg-green-100"
      : isNonRecycleAccount
        ? "bg-amber-100"
        : "bg-white";
  const noteCount = partData?.internalNotes?.length ?? 0;
  const conditionNoteCount = partData?.notes?.length ?? 0;
  const categoryMaterials = partData?.category?.partCategoryMaterials ?? partCategory.partCategory?.partCategoryMaterials ?? [];
  const partCategoryMaterialsSubtitle = categoryMaterials.length > 0
    ? categoryMaterials
      .map((link) => {
        const percentage = Number(link.materialPercentage);
        const formattedPercentage = Number.isInteger(percentage) ? percentage.toString() : percentage.toFixed(2).replace(/\.?0+$/, "");
        return `${formattedPercentage}% ${link.material?.name ?? ""}`.trim();
      })
      .join(" - ")
    : "";

  const options = [
    { id: "A", name: t("Grade A") },
    { id: "B", name: t("Grade B") },
    { id: "C", name: t("Grade C") },
    { id: "U", name: t("Grade U") },
  ]

  const handleOpenNote = (isSafety: boolean) => {
    setIsSafetySelected(isSafety);
    setOpen(true);
  }

  const handleChangeMpn = (partNumber: string) => {

    if (partNumber == partData?.partNumber) return;

    if (!partNumber && !partData?.partNumber) return;

    const body: any = {
      partId: partCategory.partDataId,
      partNumber: partNumber
    };

    patchPart.mutate(body, saveHandlers);
  }

  const handleChangeGrade = (registerName: string, grade: string) => {
    setFormValue(registerName, grade);

    if (grade == partData?.grade) return;
    if (!grade && !partData?.grade) return;

    const body: any = {
      partId: partCategory.partDataId,
      grade
    };

    patchPart.mutate(body, saveHandlers);
  }

  const handleChangeAccount = (registerName: string, accountId: string) => {
    setFormValue(registerName, accountId);
    setAccountEditing(false);

    const newAccountId = accountId || null;
    if (newAccountId === (partData?.accountId ?? null)) return;

    // Mirror the backend defaulting: when assigning an account and the part has
    // no physical routing yet, adopt the account's default route.
    const selectedAccount = flowTypeAccounts.data?.data?.find((account) => account.id === newAccountId);
    const accountRouting = selectedAccount?.physicalRoutingDefault;
    const hasNoRouting = partData?.physicalRouting == null || partData.physicalRouting === PhysicalRouting.None;
    const adoptedRouting = hasNoRouting && accountRouting != null && accountRouting !== PhysicalRouting.None
      ? accountRouting
      : undefined;

    // Update the local part immediately so actions that read it (e.g. printing
    // the label) use the new account without waiting for a job refetch.
    setPartData((prev) => (prev ? { ...prev, accountId: newAccountId, ...(adoptedRouting !== undefined ? { physicalRouting: adoptedRouting } : {}) } : prev));

    const body: any = {
      partId: partCategory.partDataId,
      accountId: newAccountId,
      updateAccount: true,
    };

    patchPart.mutate(body, saveHandlers);
  }

  const handleChangeWeight = (weightInput: string) => {
    const trimmedWeight = (weightInput ?? "").trim();
    const parsedWeight = trimmedWeight === "" ? null : Number(trimmedWeight);

    if (trimmedWeight !== "" && Number.isNaN(parsedWeight)) return;
    if (parsedWeight == partData?.weight) return;
    if (parsedWeight === null && partData?.weight == null) return;

    const body: any = {
      partId: partCategory.partDataId,
      weight: parsedWeight
    };

    patchPart.mutate(body, saveHandlers);
  }

  return (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-4xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{isSafetySelected ? t("Safety Information") : t("Dismantling Instructions")}</h1>

          <hr />

          <p className="text-gray-700">{isSafetySelected ? partCategory.partCategory?.safetyNote : partCategory.partCategory?.dismantlingInstructions}</p>
        </div>
      </Modal>

      <Modal open={gradeInfoOpen} setOpen={setGradeInfoOpen} width="max-w-2xl">
        <div className="space-y-3">
          <h1 className="text-lg font-semibold text-gray-800">{t("Grading criteria")}</h1>
          <p className="text-sm text-gray-600">{t("Requirements for each grade when assessing part condition.")}</p>

          <div className="space-y-2 text-sm text-gray-700">
            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">{t("Grade A - Excellent")}</p>
              <p>{t("Highest quality part. No, or minimal, damage, wear or corrosion. Fully functional, as new.")}</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">{t("Grade B - Good")}</p>
              <p>{t("Good condition. Small or moderate amount of damage or wear. Fully functional.")}</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">{t("Grade C - Satisfactory")}</p>
              <p>{t("Fair condition. Some wear or minor damage. Functional with possible cosmetic issues.")}</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">{t("Grade U - Not Tested/Out Of Scope")}</p>
              <p>{t("Lowest quality part. Significant wear, damage or corrosion. May have functional issues.")}</p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={storageOpen} setOpen={setStorageOpen} width="max-w-2xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{t("Storage Instructions")}</h1>
          <hr />
          <p className="text-gray-700">{partCategory.partCategory?.storageNote}</p>
        </div>
      </Modal>

      <Modal open={notesOpen} setOpen={setNotesOpen} width="max-w-3xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{t("Notes")}</h1>
          <hr />
          <NotesOutput
            notes={partData?.internalNotes ?? []}
            resourceType={ResourceType.InternalPart}
            resourceId={partCategory.partDataId ?? ""}
            refetchQuery={["parts", partData?.id]}
            shouldScroll={false}
          />
        </div>
      </Modal>

      <Modal open={conditionOpen} setOpen={setConditionOpen} width="max-w-3xl">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold">{t("Condition Notes")}</h1>
              <p className="text-sm text-gray-500">{t("These notes will go onto the listing")}</p>
            </div>
            <PartConditionPickerButton
              resourceId={partCategory.partDataId ?? ""}
              resourceType={ResourceType.Part}
              refetchQuery={["parts", partData?.id]}
            />
          </div>
          <hr />
          <NotesOutput
            notes={partData?.notes ?? []}
            resourceType={ResourceType.Part}
            resourceId={partCategory.partDataId ?? ""}
            refetchQuery={["parts", partData?.id]}
            shouldScroll={false}
          />
        </div>
      </Modal>

      <Modal open={locationOpen} setOpen={setLocationOpen} width="max-w-3xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{t("Location")}</h1>
          <hr />
          <WarehouseLocationDropdown part={partData} />
        </div>
      </Modal>

      {compact ? (
        // Details and buttons down the left; a large QR code the full height of
        // the card on the right so it scans from a distance.
        <div className={`flex flex-col gap-y-2 rounded-md p-3 ${cardBackground} ${isSelected ? "ring-2 ring-blue-500" : ""}`}>
          {/* Title spans the full card and wraps, so it never truncates on a
              narrow terminal. */}
          <div className="flex items-start gap-x-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => togglePartSelection(partData?.id)}
              className="mt-1.5 h-4 w-4 shrink-0 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-blue-500"
              disabled={!partData?.id}
            />
            <p className="min-w-0 flex-1 text-2xl font-bold leading-tight text-gray-900">{partData?.category?.name ?? partCategory.partCategory?.name}</p>
            {partCategory.partCategory?.safetyNote && (
              <button type="button" onClick={() => handleOpenNote(true)} aria-label={t("Safety")} className="shrink-0 rounded p-0.5 hover:bg-gray-100">
                <ShieldExclamationIcon className="h-4 w-4 text-amber-500" />
              </button>
            )}
            {partCategory.partCategory?.dismantlingInstructions && (
              <button type="button" onClick={() => handleOpenNote(false)} aria-label={t("Instructions")} className="shrink-0 rounded p-0.5 hover:bg-gray-100">
                <ClipboardDocumentListIcon className="h-4 w-4 text-blue-500" />
              </button>
            )}
            {partCategory.partCategory?.storageNote && (
              <button type="button" onClick={() => setStorageOpen(true)} aria-label={t("Storage")} className="shrink-0 rounded p-0.5 hover:bg-gray-100">
                <ArchiveBoxIcon className="h-4 w-4 text-purple-500" />
              </button>
            )}
          </div>

          <div className="flex gap-x-3">
          <div className="flex min-w-0 flex-1 flex-col gap-y-2">
            <div className="space-y-1">
              {/* Status only; the tag number sits under the QR code. */}
              {(partData?.isDamaged || partData?.isMissing || partData?.isNotAccessible || partData?.removedOn) && (
                <p className={classNames(
                  "text-xs font-medium",
                  partData?.isDamaged
                    ? "text-red-600"
                    : partData?.isMissing
                      ? "text-orange-600"
                      : partData?.isNotAccessible
                        ? "text-amber-600"
                        : "text-green-600"
                )}>
                  {partData?.isDamaged
                    ? t("DAMAGED")
                    : partData?.isMissing
                      ? t("MISSING")
                      : partData?.isNotAccessible
                        ? t("NOT ACCESSIBLE")
                        : t("REMOVED")}
                </p>
              )}

              {hasTmeFeature && (
                accountEditing ? (
                  <SelectInput
                    label={t("Account")}
                    register={register}
                    registerName={"accountId"}
                    options={accountOptions}
                    setValue={handleChangeAccount}
                    watch={watch}
                    placeholder={t("No Account")}
                    size="sm"
                    cols="w-full"
                    classes="rounded"
                  />
                ) : (
                  <div className="flex items-center gap-x-1 text-xs">
                    <span className={assignedAccount ? "font-medium text-gray-800" : "text-gray-500"}>
                      {assignedAccount?.name ?? t("No Account")}
                    </span>
                    <button
                      type="button"
                      onClick={() => setAccountEditing(true)}
                      className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-label={t("Change account")}
                    >
                      <PencilIcon className="h-3 w-3" />
                    </button>
                  </div>
                )
              )}
            </div>

            {!partData?.isDamaged && !partData?.isMissing && !partData?.isNotAccessible && !partData?.removedOn ? (
              <>
                <ButtonInput
                  label={t("Removed")}
                  isSubmit={false}
                  onClick={() => markAsRemoved(partData!)}
                  classes="w-full !py-1.5 !bg-green-600 hover:!bg-green-700"
                />
                <div className="grid grid-cols-3 gap-2">
                  <ButtonInput
                    label={t("Damaged")}
                    isSubmit={false}
                    onClick={() => { setDamaged(partData); setDamagedOpen(true); }}
                    classes="w-full !px-1 !py-1 !text-xs !bg-red-600 hover:!bg-red-700"
                  />
                  <ButtonInput
                    label={t("Missing")}
                    isSubmit={false}
                    onClick={() => { setMissing(partData); setMissingOpen(true); }}
                    classes="w-full !px-1 !py-1 !text-xs !bg-orange-600 hover:!bg-orange-700"
                  />
                  <ButtonInput
                    label={t("Not Accessible")}
                    isSubmit={false}
                    onClick={() => markNotAccessible(partData)}
                    classes="w-full !px-1 !py-1 !text-xs !bg-amber-600 hover:!bg-amber-700"
                  />
                </div>
              </>
            ) : (
              // Who dealt with it and when, in place of the buttons.
              <div className="flex items-center justify-center gap-x-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700">
                {(() => {
                  const on = partData?.removedOn ?? partData?.damageReportedOn;
                  const by = partData?.removedOn ? partData.removedBy : partData?.damageReportedBy;
                  return (
                    <>
                      {on && <span>{formatDateTime(on, DateTime.TIME_SIMPLE)}</span>}
                      {on && by && <span>·</span>}
                      {by && <ContactFromId contactId={by} />}
                    </>
                  );
                })()}
              </div>
            )}

            <button
              type="button"
              onClick={() => handlePrint(partData!)}
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("Print Label")}
            </button>
          </div>

          {partData?.id && (
            <div className="flex shrink-0 flex-col items-center justify-center">
              <ScanQrCode value={partData.id} alt={t("Part QR code")} size={200} className="h-28 w-28" />
              <span className="text-[10px] leading-tight text-gray-500">{partData.tagNumber}</span>
            </div>
          )}
          </div>
        </div>
      ) : (
      <div key={partCategory.partDataId} className={`p-4 rounded-md flex flex-col gap-y-3 ${cardBackground} ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>

        <div className="flex gap-x-2 items-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => togglePartSelection(partData?.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            disabled={!partData?.id}
          />
          <p className="text-lg font-semibold">{partData?.category?.name} - <span className="text-base font-medium">{partData?.tagNumber}</span></p>

          {partData?.removedOn && (
            <p className="font-semibold text-green-600">{t("REMOVED")}</p>
          )}
          {partData?.isDamaged && (
            <p className="font-semibold text-red-600">{t("DAMAGED")}</p>
          )}
          {partData?.isMissing && (
            <p className="font-semibold text-orange-600">MISSING</p>
          )}
          {partData?.isNotAccessible && (
            <p className="font-semibold text-amber-600">{t("NOT ACCESSIBLE")}</p>
          )}
          {partData?.isCore && (
            <RsCoreImage className="ml-auto !h-6" />
          )}
        </div>

        {partData?.physicalRouting != null && partData.physicalRouting !== PhysicalRouting.None && (
          <p className="text-sm text-gray-600">{t(PhysicalRouting[partData.physicalRouting])}</p>
        )}

        {partCategoryMaterialsSubtitle && (
          <p className="text-sm text-gray-600">
            {partCategoryMaterialsSubtitle}
          </p>
        )}

        <div className="flex justify-between items-start gap-x-4">
          <div className="flex flex-col gap-y-3">
            <div className="flex flex-wrap gap-2">
              {partCategory.partCategory?.safetyNote &&
                <button
                  onClick={() => handleOpenNote(true)}
                  className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
                >
                  <ShieldExclamationIcon className="h-4 w-4 text-amber-500" /> {t("Safety")}
                </button>
              }

              {partCategory.partCategory?.dismantlingInstructions &&
                <button
                  onClick={() => handleOpenNote(false)}
                  className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4 text-blue-500" /> {t("Instructions")}
                </button>
              }

              {partCategory.partCategory?.storageNote &&
                <button
                  onClick={() => setStorageOpen(true)}
                  className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
                >
                  <ArchiveBoxIcon className="h-4 w-4 text-purple-500" /> {t("Storage")}
                </button>
              }

              <button
                onClick={() => setNotesOpen(true)}
                className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
              >
                <PencilSquareIcon className="h-4 w-4 text-green-500" /> {t("Notes")} {noteCount > 0 && `(${noteCount})`}
              </button>

              <button
                onClick={() => setConditionOpen(true)}
                className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
              >
                <DocumentTextIcon className="h-4 w-4 text-primary-500" /> {t("Condition")} {conditionNoteCount > 0 && `(${conditionNoteCount})`}
              </button>

              <button
                onClick={() => setLocationOpen(true)}
                className="flex gap-x-1 items-center text-xs text-gray-600 hover:bg-gray-100 rounded-lg p-1"
              >
                <MapPinIcon className="h-4 w-4 text-amber-500" /> {t("Location")}
              </button>

            </div>

            {hasTmeFeature && (
              <div>
                <SelectInput
                  label={t("Account")}
                  register={register}
                  registerName={"accountId"}
                  options={accountOptions}
                  setValue={handleChangeAccount}
                  watch={watch}
                  labelPosition="inline"
                  placeholder={t("No Account")}
                  size="sm"
                  cols="w-64"
                  classes="rounded"
                />
              </div>
            )}
          </div>

          {partData?.id && (
            <ScanQrCode value={partData.id} alt={t("Part QR code")} size={150} className="h-24 w-24 shrink-0" />
          )}
        </div>

        <div>
          <p>{t("OE MPN")}: {partData?.oePartNumber ?? "-"}</p>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-2 text-gray-700">


          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-500">{t("MPN")}</label>
            <input
              onBlur={(e) => handleChangeMpn(e.target.value)}
              type="string"
              defaultValue={partData?.partNumber ?? ""}
              className="px-2 py-1 text-sm rounded border border-gray-300"
              placeholder={t("Add MPN")}
            />
          </div>

          <div className="flex items-center gap-x-2">
            <SelectInput
              label={t("Grade")}
              register={register}
              registerName={"grade"}
              options={options}
              setValue={handleChangeGrade}
              watch={watch}
              labelPosition="inline"
              placeholder={t("Select Grade")}
              size="sm"
              cols="w-48"
              classes="rounded"
            />
            <button
              type="button"
              onClick={() => setGradeInfoOpen(true)}
              className="text-gray-500 hover:text-gray-700"
              aria-label={t("Open grading criteria")}
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-500">{t("Weight")}</label>
            <input
              onBlur={(e) => handleChangeWeight(e.target.value)}
              type="number"
              step=".01"
              defaultValue={partData?.weight ?? ""}
              className="px-2 py-1 text-sm rounded border border-gray-300 w-28"
              placeholder="kg"
            />
          </div>
        </div>

        {partData?.id && dismantlerAspectNames.length > 0 && (
          <JobPartAspects
            partDataId={partData.id}
            ebayCategoryId={ebayCategoryId}
            dismantlerAspectNames={dismantlerAspectNames}
            categoryCustomAspects={partCategoryDefault?.categoryCustomAspects ?? []}
            existingAspects={partData.aspects ?? []}
            // onSaved={() => singlePart.refetch()}
          />
        )}

        <hr />

        <div className="flex items-center gap-x-3">
          {onDiagramClick && partCategory.partCategory && (() => {
            const canView = partCategory.partCategory!.ucidZone && partData?.matchedUcid;
            return (
              <ButtonInput
                label={canView ? t("View Diagram") : t("Find Diagram")}
                isSubmit={false}
                onClick={() => onDiagramClick(partCategory)}
                classes={classNames("!py-1 !px-1 ", canView
                  ? "!bg-primary-100 !text-primary-700 hover:!bg-primary-200"
                  : "!bg-gray-200 !text-gray-600 hover:!bg-gray-300")}
              />
            );
          })()}
        </div>

        <div className="flex flex-col flex-wrap md:flex-row gap-x-4 gap-y-2">

          {!partData?.isDamaged && !partData?.isMissing && !partData?.isNotAccessible && (
            <>
              <ButtonInput
                label={t("Mark Removed")}
                isSubmit={false}
                onClick={() => markAsRemoved(partData!)}
                classes={classNames(partData?.removedOn ? "" : "!bg-green-600 hover:!bg-green-700", "!py-1 !px-1")}
                disabled={partData?.removedOn !== null}
              />
              <ButtonInput
                label={t("Mark Damaged")}
                isSubmit={false}
                onClick={() => { setDamaged(partData); setDamagedOpen(true); }}
                classes={"!py-1 !px-1 !bg-red-600 hover:!bg-red-700"}
              />
              <ButtonInput
                label={t("Mark Missing")}
                isSubmit={false}
                onClick={() => { setMissing(partData); setMissingOpen(true); }}
                classes={"!py-1 !px-1 !bg-orange-600 hover:!bg-orange-700"}
              />
              <ButtonInput
                label={t("Mark Not Accessible")}
                isSubmit={false}
                onClick={() => markNotAccessible(partData)}
                classes={"!py-1 !px-1 !bg-amber-600 hover:!bg-amber-700"}
                disabled={partData?.removedOn !== null}
              />
            </>
          )}

          <ButtonInput
            label={t("View Stock")}
            isSubmit={false}
            onClick={() => openInNewTab(`/stock/${partData?.id}`)}
            classes={"!py-1 !px-1 "}
          />

          <ButtonInput
            label={t("Print Label")}
            isSubmit={false}
            onClick={() => handlePrint(partData!)}
            classes={"!py-1 !px-1 "}
          />
        </div>
      </div>
      )}
    </>
  )
}